import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  ArrowRight, Building2, ChevronDown, Lightbulb, MessageSquare, Repeat,
  Workflow, Wrench, Boxes, Sparkles,
} from 'lucide-react';
import { Action, Chip, EmptyState, FilterChips, Panel, Stat } from '../../components/aa';
import type { ChipTone } from '../../components/aa';
import {
  LANES, LANE_TONE, PROJECT_STATUS, laneLabel, percent, plural, shortDate,
} from './adoption';
import type { CompanyPerson, CompanyView, Opportunity, SessionRow, Stats, Tool, Work } from './adoption';

const Bar: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-canvas-inset" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
    <div className="h-full rounded-full bg-ink transition-[width] duration-300" style={{ width: `${value}%` }} />
  </div>
);

const decideLine = (stats: Stats, clientView: boolean): string => {
  if (stats.opportunities === 0) return 'No open opportunities yet';
  if (stats.decide === 0 && clientView) return 'Nothing waiting on your decision';
  if (stats.decide === 0) return 'Every opportunity has been proposed';
  if (clientView) return `${stats.decide} waiting on your decision`;
  return `${stats.decide} not proposed yet`;
};

const sessionsLine = (stats: Stats): string => {
  if (stats.sessions === 0) return 'No sessions sent yet';
  if (stats.sessions_open === 0 && stats.sessions === 1) return 'The session is fully answered';
  if (stats.sessions_open === 0) return `All ${stats.sessions} sessions fully answered`;
  return `${plural(stats.sessions_open, 'session', 'sessions')} still waiting on answers`;
};

export const Tiles: React.FC<{ stats: Stats; clientView: boolean }> = ({ stats, clientView }) => {
  let opportunityTone: 'default' | 'attention' = 'default';
  if (stats.decide > 0) opportunityTone = 'attention';
  const answered = percent(stats.answered, stats.asked);
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2">
      <Stat label="Open opportunities" value={stats.opportunities} tone={opportunityTone} evidence={decideLine(stats, clientView)} />
      <Stat
        label="Session answers"
        value={(
          <span>
            {stats.answered}
            <span className="text-[18px] font-medium text-ink-dim sm:text-[20px]"> of {stats.asked}</span>
          </span>
        )}
        evidence={(
          <span className="block">
            <span className="mb-1.5 mt-1 block"><Bar value={answered} label={`${answered}% of session answers in`} /></span>
            {sessionsLine(stats)}
          </span>
        )}
      />
    </div>
  );
};


const KIND_ICON: Record<Opportunity['kind'], React.ReactNode> = {
  problem: <Lightbulb size={16} aria-hidden />,
  request: <MessageSquare size={16} aria-hidden />,
  tool: <Wrench size={16} aria-hidden />,
};

type OpportunityItem = Opportunity & { company?: CompanyView };

const metaLine = (o: OpportunityItem, showCompany: boolean): string => {
  const parts: string[] = [];
  if (showCompany && o.company) parts.push(o.company.name);
  parts.push(o.source);
  if (o.created_by) parts.push(`by ${o.created_by.name}`);
  if (o.created_at) parts.push(shortDate(o.created_at));
  return parts.join(' · ');
};

export const OpportunityList: React.FC<{
  companies: CompanyView[];
  clientView: boolean;
  showCompany: boolean;
  root: string;
}> = ({ companies, clientView, showCompany, root }) => {
  const navigate = useNavigate();
  const [lane, setLane] = useState<string>('all');
  const open = (o: OpportunityItem) => {
    if (!o.company) return;
    navigate(`${root}/${o.company.id}/opportunities/${o.kind}/${encodeURIComponent(o.id)}`);
  };
  const items: OpportunityItem[] = useMemo(() => {
    const all: OpportunityItem[] = [];
    companies.forEach((c) => c.opportunities.forEach((o) => all.push({ ...o, company: c })));
    all.sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
    return all;
  }, [companies]);

  const tabs = [{ id: 'all', label: 'All', count: items.length }];
  LANES.forEach((l) => {
    const count = items.filter((o) => o.lane === l).length;
    if (count > 0) tabs.push({ id: l, label: laneLabel(l, clientView), count });
  });
  const shown = items.filter((o) => lane === 'all' || o.lane === lane);

  if (items.length === 0) {
    let reason = 'Opportunities appear here when Orbit finds one in the company\'s answers, or when someone asks for something.';
    if (!clientView) reason = 'Send a session or finish the company intake, and Orbit turns what people say into opportunities here.';
    return <Panel><EmptyState title="No opportunities yet" reason={reason} /></Panel>;
  }

  return (
    <div>
      <FilterChips tabs={tabs} active={lane} onChange={setLane} label="Filter opportunities by status" />
      <Panel>
        <ul className="m-0 list-none divide-y divide-line-soft p-0">
          {shown.map((o) => (
            <li key={`${o.company?.id}-${o.kind}-${o.id}`}>
              <button type="button" onClick={() => open(o)} className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-canvas focus-visible:bg-canvas focus-visible:outline-none sm:px-6">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-canvas-inset text-ink-muted">{KIND_ICON[o.kind]}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-semibold leading-[20px] text-ink" title={o.title}>{o.title}</span>
                  <span className="mt-0.5 block truncate text-[12px] leading-[17px] text-ink-dim">
                    {metaLine(o, showCompany)}
                  </span>
                </span>
                <span className="hidden shrink-0 sm:block"><Chip tone={LANE_TONE[o.lane]} dot>{laneLabel(o.lane, clientView)}</Chip></span>
              </button>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
};


const sessionTone = (s: SessionRow): ChipTone => {
  if (s.recipients > 0 && s.answered >= s.recipients) return 'ok';
  return 'run';
};

const sessionLabel = (s: SessionRow): string => {
  if (s.recipients > 0 && s.answered >= s.recipients) return 'All answered';
  return `${s.recipients - s.answered} waiting`;
};

const SessionPeople: React.FC<{ session: SessionRow }> = ({ session }) => (
  <ul className="m-0 mt-3 list-none divide-y divide-line-soft rounded-card border border-line-soft p-0">
    {session.people.map((p) => (
      <li key={p.email} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
        <span className="min-w-0">
          <b className="block truncate text-[13px] text-ink">{p.name}</b>
          <small className="block truncate text-[12px] text-ink-dim">{p.email}</small>
        </span>
        <span className="flex items-center gap-2">
          {!p.onboarded && <Chip>No login yet</Chip>}
          {p.answered_at && <Chip tone="ok" dot>Answered {shortDate(p.answered_at)}</Chip>}
          {!p.answered_at && <Chip tone="warn" dot>Waiting</Chip>}
        </span>
      </li>
    ))}
  </ul>
);

export const SessionList: React.FC<{
  companies: CompanyView[];
  showCompany: boolean;
  answersLink?: (companyId: string, sessionId: string) => string | null;
  emptyAction?: React.ReactNode;
}> = ({ companies, showCompany, answersLink, emptyAction }) => {
  const [expanded, setExpanded] = useState<string>('');
  const rows = useMemo(() => {
    const all: (SessionRow & { company: CompanyView })[] = [];
    companies.forEach((c) => c.sessions.forEach((s) => all.push({ ...s, company: c })));
    all.sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
    return all;
  }, [companies]);

  if (rows.length === 0) {
    return (
      <Panel>
        <EmptyState
          title="No sessions sent yet"
          reason="A session is a short set of questions the Solution Partner sends to people at the company. Their answers show here as they come in."
          action={emptyAction}
        />
      </Panel>
    );
  }

  return (
    <Panel>
      <ul className="m-0 list-none divide-y divide-line-soft p-0">
        {rows.map((s) => {
          const key = `${s.company.id}-${s.id}`;
          const isOpen = expanded === key;
          const share = percent(s.answered, s.recipients);
          let link: string | null = null;
          if (answersLink) link = answersLink(s.company.id, s.id);
          let toggleLabel = 'Show who answered';
          let nextExpanded = key;
          if (isOpen) {
            toggleLabel = 'Hide people';
            nextExpanded = '';
          }
          return (
            <li key={key} className="px-5 py-4 sm:px-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {showCompany && <p className="m-0 mb-1 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{s.company.name}</p>}
                  <p className="m-0 text-[14.5px] font-semibold leading-[20px] text-ink">{s.title}</p>
                  <p className="m-0 mt-1 text-[12.5px] text-ink-dim">
                    {[s.sender && `Sent by ${s.sender}`, shortDate(s.sent_at)].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <Chip tone={sessionTone(s)} dot>{sessionLabel(s)}</Chip>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-[160px] flex-1"><Bar value={share} label={`${share}% answered`} /></div>
                <span className="text-[12.5px] tabular-nums text-ink-muted">{s.answered} of {s.recipients} answered</span>
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExpanded(nextExpanded)}
                  aria-expanded={isOpen}
                  className="inline-flex min-h-[32px] items-center gap-1 rounded-act px-1 text-[12.5px] font-semibold text-ink-muted hover:text-ink"
                >
                  {toggleLabel}
                  <ChevronDown size={14} className={clsx('transition-transform', { 'rotate-180': isOpen })} aria-hidden />
                </button>
                {link && <Action size="sm" variant="ghost" to={link} arrow>Read the answers</Action>}
              </div>
              {isOpen && <SessionPeople session={s} />}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
};


const Ratio: React.FC<{ part: number; whole: number; empty: string }> = ({ part, whole, empty }) => {
  if (!whole) return <span className="text-ink-soft">{empty}</span>;
  return (
    <span className="block">
      <span className="tabular-nums text-ink">{part}</span>
      <span className="tabular-nums text-ink-dim"> of {whole}</span>
      <span className="mt-1.5 block w-full max-w-[120px]"><Bar value={percent(part, whole)} label={`${percent(part, whole)}%`} /></span>
    </span>
  );
};

const COLUMNS = 'grid grid-cols-[minmax(200px,1.6fr)_repeat(4,minmax(110px,1fr))_20px] items-center gap-4';

export const ClientTable: React.FC<{ companies: CompanyView[]; onOpen: (id: string) => void }> = ({ companies, onOpen }) => {
  if (companies.length === 0) {
    return <Panel><EmptyState title="No clients yet" reason="Clients you add show here with their sessions, opportunities and the work they turned into." /></Panel>;
  }
  return (
    <Panel>
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className={`${COLUMNS} border-b border-line-soft px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft`}>
            <div>Company</div>
            <div>Session answers</div>
            <div>Opportunities</div>
            <div>Tools connected</div>
            <div>People set up</div>
            <span />
          </div>
          {companies.map((c) => (
            <button key={c.id} type="button" onClick={() => onOpen(c.id)} className={`${COLUMNS} w-full border-t border-line-soft px-6 py-3.5 text-left text-[13px] first:border-t-0 hover:bg-canvas focus-visible:bg-canvas focus-visible:outline-none`}>
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-ink text-[11px] font-bold text-white">{c.initials}</span>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-ink">{c.name}</span>
                  {c.own && <span className="block text-[11.5px] text-ink-dim">Your company</span>}
                </span>
              </span>
              <Ratio part={c.stats.answered} whole={c.stats.asked} empty="None sent" />
              <span>
                <span className="tabular-nums text-ink">{c.stats.opportunities}</span>
                {c.stats.decide > 0 && <span className="block text-[11.5px] text-ink-dim">{c.stats.decide} not proposed</span>}
              </span>
              <Ratio part={c.stats.tools_connected} whole={c.stats.tools_used} empty="None named" />
              <Ratio part={c.stats.onboarded} whole={c.stats.people} empty="Nobody yet" />
              <ArrowRight size={16} className="text-ink-soft" aria-hidden />
            </button>
          ))}
        </div>
      </div>
    </Panel>
  );
};


const ToolMark: React.FC<{ tool: Tool }> = ({ tool }) => {
  const [broken, setBroken] = useState(false);
  if (tool.logo && !broken) {
    return <img src={tool.logo} alt="" className="h-8 w-8 shrink-0 rounded-lg border border-line-soft bg-white object-contain p-1" onError={() => setBroken(true)} />;
  }
  return <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-canvas-inset text-[11px] font-bold text-ink-muted" aria-hidden>{tool.label.slice(0, 2).toUpperCase()}</span>;
};

const connectionLine = (tool: Tool): string => {
  const org = tool.connections.find((c) => c.scope === 'organization');
  if (org && org.by) return `Connected for the whole company by ${org.by.name}`;
  if (org) return 'Connected for the whole company';
  const people = tool.connections.filter((c) => c.scope !== 'organization');
  if (people.length === 1 && people[0].by) return `Connected by ${people[0].by.name}`;
  return `Connected by ${plural(people.length, 'person', 'people')}`;
};

const ADDED = 'Added to the company\'s tools';


type ToolStatus = 'not_connected' | 'connected' | 'unreachable';

const TOOL_STATUS: Record<ToolStatus, { label: string; tone: ChipTone }> = {
  not_connected: { label: 'Used, not connected', tone: 'warn' },
  connected: { label: 'Connected', tone: 'ok' },
  unreachable: { label: 'Not connectable yet', tone: 'neutral' },
};

const whoLine = (tool: Tool): string => {
  if (tool.connected) return connectionLine(tool);
  if (tool.added_by) return `Added by ${tool.added_by.name}`;
  return 'Nobody yet';
};

const howLine = (tool: Tool): string => {
  const said = tool.said_by.filter((x) => x !== ADDED);
  if (said.length) return said.join(' · ');
  if (tool.added_by || tool.said_by.includes(ADDED)) return 'Added to the company\'s tools';
  return 'Connected directly';
};

const TOOL_COLUMNS = 'grid grid-cols-[minmax(180px,1.2fr)_170px_minmax(180px,1.2fr)_minmax(200px,1.4fr)] items-center gap-4';

export const ToolsPanel: React.FC<{ tools: NonNullable<CompanyView['tools']> }> = ({ tools }) => {
  const [filter, setFilter] = useState<string>('all');
  const rows: { tool: Tool; status: ToolStatus }[] = [
    ...tools.not_connected.map((tool) => ({ tool, status: 'not_connected' as ToolStatus })),
    ...tools.connected.map((tool) => ({ tool, status: 'connected' as ToolStatus })),
    ...tools.unreachable.map((tool) => ({ tool, status: 'unreachable' as ToolStatus })),
  ];
  if (rows.length === 0) {
    return <Panel><EmptyState title="No tools yet" reason="Tools show here once the company names them in its intake, picks them in setup, or connects them." /></Panel>;
  }
  const tabs = [{ id: 'all', label: 'All', count: rows.length }];
  (Object.keys(TOOL_STATUS) as ToolStatus[]).forEach((st) => {
    const count = rows.filter((r) => r.status === st).length;
    if (count > 0) tabs.push({ id: st, label: TOOL_STATUS[st].label, count });
  });
  const shown = rows.filter((r) => filter === 'all' || r.status === filter);
  return (
    <div>
      <FilterChips tabs={tabs} active={filter} onChange={setFilter} label="Filter tools by status" />
      <Panel>
        <div className="overflow-x-auto">
          <div className="min-w-[780px]">
            <div className={`${TOOL_COLUMNS} border-b border-line-soft px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft`}>
              <div>Tool</div>
              <div>Status</div>
              <div>Who</div>
              <div>How we know about it</div>
            </div>
            {shown.map(({ tool, status }) => (
              <div key={tool.key} className={`${TOOL_COLUMNS} border-t border-line-soft px-6 py-3 text-[13px] first:border-t-0`}>
                <span className="flex min-w-0 items-center gap-3">
                  <ToolMark tool={tool} />
                  <span className="truncate font-semibold text-ink">{tool.label}</span>
                </span>
                <span><Chip tone={TOOL_STATUS[status].tone} dot>{TOOL_STATUS[status].label}</Chip></span>
                <span className="truncate text-ink-muted" title={whoLine(tool)}>{whoLine(tool)}</span>
                <span className="truncate text-ink-dim" title={howLine(tool)}>{howLine(tool)}</span>
              </div>
            ))}
          </div>
        </div>
      </Panel>
      <p className="m-0 mt-3 px-1 text-[12px] leading-[18px] text-ink-dim">
        Used, not connected: the company works in it, but Orbit and its skills cannot reach it yet. Not connectable yet: there is no connector for it today.
      </p>
    </div>
  );
};

const WorkGroup: React.FC<{ title: string; icon: React.ReactNode; count: number; empty: string; children: React.ReactNode }> = ({ title, icon, count, empty, children }) => (
  <Panel>
    <div className="flex items-center justify-between gap-3 border-b border-line-soft px-5 py-3.5 sm:px-6">
      <span className="flex items-center gap-2.5 text-ink">
        <span className="text-ink-muted">{icon}</span>
        <h3 className="m-0 text-[15px] font-bold">{title}</h3>
      </span>
      <span className="text-[12.5px] tabular-nums text-ink-dim">{count}</span>
    </div>
    {count === 0 && <p className="m-0 px-6 py-5 text-[13px] text-ink-dim">{empty}</p>}
    {count > 0 && <ul className="m-0 list-none divide-y divide-line-soft p-0">{children}</ul>}
  </Panel>
);

const WorkRow: React.FC<{ title: string; meta: string; chip: React.ReactNode }> = ({ title, meta, chip }) => (
  <li className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 sm:px-6">
    <span className="min-w-0 flex-1">
      <b className="block truncate text-[13.5px] text-ink">{title}</b>
      {meta && <small className="block truncate text-[12px] text-ink-dim">{meta}</small>}
    </span>
    {chip}
  </li>
);

const byLine = (who: { name: string } | null): string => {
  if (!who) return '';
  return `By ${who.name}`;
};

export const WorkPanel: React.FC<{ work: Work }> = ({ work }) => (
  <div className="grid items-start gap-4 lg:grid-cols-2">
    <WorkGroup title="Projects" icon={<Boxes size={18} aria-hidden />} count={work.projects.length} empty="No projects in the builder yet.">
      {work.projects.map((p) => {
        const status = PROJECT_STATUS[p.status] || { label: p.status || 'Unknown', tone: 'neutral' as ChipTone };
        return <WorkRow key={p.id} title={p.title} meta="" chip={<Chip tone={status.tone} dot>{status.label}</Chip>} />;
      })}
    </WorkGroup>
    <WorkGroup title="Routines" icon={<Repeat size={18} aria-hidden />} count={work.routines.length} empty="No routines set up yet.">
      {work.routines.map((r) => {
        let chip = <Chip tone="ok" dot>Running {r.cadence}</Chip>;
        if (!r.running) chip = <Chip>Paused</Chip>;
        return <WorkRow key={r.id} title={r.title} meta={byLine(r.created_by)} chip={chip} />;
      })}
    </WorkGroup>
    <WorkGroup title="Workflows" icon={<Workflow size={18} aria-hidden />} count={work.workflows.length} empty="No workflows yet.">
      {work.workflows.map((w) => {
        let chip = <Chip tone="ok" dot>Live</Chip>;
        if (!w.running) chip = <Chip>Draft</Chip>;
        return <WorkRow key={w.id} title={w.title} meta={w.channel && `On ${w.channel}`} chip={chip} />;
      })}
    </WorkGroup>
    <WorkGroup title="Skills" icon={<Sparkles size={18} aria-hidden />} count={work.skills.length} empty="No company skills yet.">
      {work.skills.map((s) => {
        let chip = <Chip tone="ok" dot>Published</Chip>;
        if (!s.published) chip = <Chip>Draft</Chip>;
        return <WorkRow key={s.id} title={s.title} meta={byLine(s.created_by)} chip={chip} />;
      })}
    </WorkGroup>
  </div>
);


export const PeoplePanel: React.FC<{ people: CompanyPerson[] }> = ({ people }) => {
  if (people.length === 0) {
    return <Panel><EmptyState title="No people yet" reason="People added to the company show here, with whether they have set up their login." /></Panel>;
  }
  return (
    <Panel>
      <ul className="m-0 list-none divide-y divide-line-soft p-0">
        {people.map((p) => (
          <li key={p.email} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 sm:px-6">
            <span className="min-w-0 flex-1">
              <b className="block truncate text-[13.5px] text-ink">{p.name}</b>
              <small className="block truncate text-[12px] text-ink-dim">{[p.role, p.department, p.email].filter(Boolean).join(' · ')}</small>
            </span>
            <span className="flex flex-wrap items-center gap-2">
              {p.sessions_answered > 0 && <Chip>{plural(p.sessions_answered, 'session answered', 'sessions answered')}</Chip>}
              {p.onboarded && <Chip tone="ok" dot>Set up</Chip>}
              {!p.onboarded && <Chip tone="warn" dot>Not set up yet</Chip>}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
};

export const CompanyMark: React.FC<{ company: CompanyView }> = ({ company }) => (
  <span className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-tile bg-ink text-[15px] font-bold text-white">
    {company.initials || <Building2 size={18} aria-hidden />}
  </span>
);
